import React, { useEffect, useState } from "react";
import { Menu, Sun, Moon } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/SiteContext";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const LangToggle = () => {
  const { lang, setLang } = useSite();
  return (
    <div className="flex items-center gap-1 font-body text-sm" data-testid="lang-toggle">
      <button
        data-testid="lang-toggle-id"
        onClick={() => setLang("id")}
        className={`px-1 transition-colors ${lang === "id" ? "font-semibold text-gold" : "text-muted-foreground hover:text-foreground"}`}
      >
        ID
      </button>
      <span className="text-muted-foreground">/</span>
      <button
        data-testid="lang-toggle-en"
        onClick={() => setLang("en")}
        className={`px-1 transition-colors ${lang === "en" ? "font-semibold text-gold" : "text-muted-foreground hover:text-foreground"}`}
      >
        EN
      </button>
    </div>
  );
};

const Navbar = () => {
  const { t, theme, toggleTheme } = useSite();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.why },
    { id: "process", label: t.nav.process },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-border py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 lg:px-12">
        <Logo onClick={() => scrollTo("hero")} />

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="group relative font-body text-sm font-light uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <LangToggle />
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="text-muted-foreground transition-colors hover:text-gold"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("contact")}
            className="hidden bg-gold px-5 py-2.5 font-body text-xs font-medium uppercase tracking-[0.12em] text-black transition-opacity hover:opacity-85 md:block"
          >
            {t.nav.cta}
          </button>

          {/* Mobile */}
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" className="text-foreground lg:hidden" aria-label="Menu">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[80vw] border-border bg-background">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <nav className="mt-10 flex flex-col gap-2">
                {links.map((l) => (
                  <SheetClose asChild key={l.id}>
                    <button
                      data-testid={`mobile-nav-${l.id}`}
                      onClick={() => scrollTo(l.id)}
                      className="border-b border-border py-4 text-left font-heading text-2xl text-foreground transition-colors hover:text-gold"
                    >
                      {l.label}
                    </button>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <button
                    onClick={() => scrollTo("contact")}
                    className="mt-6 bg-gold py-4 font-body text-sm font-medium uppercase tracking-[0.12em] text-black"
                  >
                    {t.nav.cta}
                  </button>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
